import { useEffect, useState } from "react";
import { Copy, KeyRound, Shuffle, Power, Smartphone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const waPhone = (m) => {
  let d = (m || "").replace(/\D/g, "").replace(/^00/, "");
  if (d.startsWith("05") && d.length === 10) { d = `966${d.slice(1)}`; }
  else if (d.startsWith("5") && d.length === 9) { d = `966${d}`; }
  return d;
};

const WhatsAppShare = ({ client, url, pin }) => {
  const [phone, setPhone] = useState(client.mobile || "");
  const send = () => {
    const msg = [`Dear ${client.contact_person || client.company_name},`, "", `Your VAT client portal for ${client.company_name} is ready. Upload purchase bills and submit sales from your phone:`, url,
      pin ? `PIN: ${pin}` : "Use the PIN shared with you to sign in.", "", "Tip: tap \"Add to home screen\" to open it like an app."].join("\n");
    window.open(`https://wa.me/${waPhone(phone)}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };
  return (
    <div className="flex gap-2" data-testid="portal-whatsapp">
      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Client WhatsApp no. (e.g. 05XXXXXXXX)" className="num" inputMode="tel" data-testid="portal-whatsapp-phone" />
      <Button onClick={send} className="bg-[#25D366] hover:bg-[#1ebe5b] text-white shrink-0" data-testid="portal-whatsapp-send"><MessageCircle className="w-4 h-4 mr-1.5" />{pin ? "Send link + PIN" : "Send link"}</Button>
    </div>
  );
};

const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"));

export default function PortalDialog({ client, onOpenChange }) {
  const [st, setSt] = useState(null);
  const [pin, setPin] = useState("");
  const [shown, setShown] = useState("");
  useEffect(() => { if (client) { setShown(""); setPin(""); api.get(`/clients/${client.id}/portal`).then(({ data }) => setSt(data)); } }, [client]);
  const url = st?.code ? `${window.location.origin}/portal/${st.code}` : "";

  const setPinReq = async (custom) => {
    try {
      const { data } = await api.post(`/clients/${client.id}/portal`, { pin: custom || null });
      setSt({ ...st, ...data }); setShown(data.pin); setPin(""); toast.success("PIN updated — previous sessions signed out");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const disable = async () => { await api.post(`/clients/${client.id}/portal/disable`); setSt({ ...st, enabled: false }); setShown(""); toast.success("Portal disabled"); };

  return (
    <Dialog open={!!client} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="portal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-emerald-700" />Client portal · {client?.company_name}</DialogTitle>
          <DialogDescription>A unique link + PIN lets the client upload purchase bills and submit sales from their phone. Sessions last 1 hour.</DialogDescription>
        </DialogHeader>
        {st && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">Status: <span className={`font-medium ${st.enabled ? "text-emerald-700" : "text-slate-500"}`} data-testid="portal-status">{st.enabled ? "Enabled" : "Not enabled"}</span>
              {st.updated_at && <span className="text-[11px] text-slate-400 num">· PIN set {fmtDateTime(st.updated_at)}</span>}</div>
            {url && st.enabled && (
              <div className="flex gap-2"><Input readOnly value={url} className="num text-xs" data-testid="portal-url" /><Button variant="outline" onClick={() => copy(url)} data-testid="portal-copy-url"><Copy className="w-4 h-4" /></Button></div>
            )}
            {client && url && st.enabled && <WhatsAppShare key={client.id} client={client} url={url} pin={shown} />}
            {shown && (
              <div className="border border-emerald-200 bg-emerald-50 rounded p-3 flex items-center justify-between" data-testid="portal-pin-shown">
                <div><div className="text-[11px] text-emerald-700 uppercase tracking-wider">New PIN — share securely, shown once</div><div className="num text-2xl font-semibold tracking-[0.3em]">{shown}</div></div>
                <Button variant="outline" size="sm" onClick={() => copy(`${url}\nPIN: ${shown}`)} data-testid="portal-copy-pin"><Copy className="w-4 h-4 mr-1" />Copy link + PIN</Button>
              </div>
            )}
            <Button onClick={() => setPinReq(null)} className="w-full bg-emerald-800 hover:bg-emerald-900" data-testid="portal-generate-pin"><Shuffle className="w-4 h-4 mr-1.5" />{st.code ? "Generate new random PIN" : "Generate client URL + PIN"}</Button>
            <div className="flex gap-2">
              <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="Or type a PIN (4–8 digits)" className="num" inputMode="numeric" data-testid="portal-custom-pin" />
              <Button variant="outline" disabled={pin.length < 4} onClick={() => setPinReq(pin)} data-testid="portal-set-pin"><KeyRound className="w-4 h-4 mr-1.5" />Set PIN</Button>
            </div>
            {st.enabled && <Button variant="ghost" className="text-red-600 w-full" onClick={disable} data-testid="portal-disable"><Power className="w-4 h-4 mr-1.5" />Disable portal</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
